import { useState } from "react";
import { imageTypes } from "../constants/lists";
import { processImageForAvatar } from "../utils/imageUtils";

export const useImageUpload = ({ onUpload, onError, maxSize = 10 * 1024 * 1024 }) => {
  const [profileImageURL, setProfileImageURL] = useState(null);
  const [cropperOpen, setCropperOpen] = useState(false);
  const [tempImageSrc, setTempImageSrc] = useState(null);
  const [tempFile, setTempFile] = useState(null);
  const [error, setError] = useState("");

  const handleFileRead = (file) => {
    if (!file || !file.type?.startsWith("image/")) {
      setProfileImageURL(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      setProfileImageURL(event.target.result);
    };
    reader.onerror = () => {
      onError?.("There is an issue while reading this file");
    };
    reader.readAsDataURL(file);
  };

  const convertSvgToRaster = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Set fixed dimensions for SVG
          const size = 400;
          canvas.width = size;
          canvas.height = size;
          
          // Fill with white background
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, size, size);
          
          // Draw the SVG image
          ctx.drawImage(img, 0, 0, size, size);
          
          canvas.toBlob((blob) => {
            if (blob) {
              const rasterFile = new File([blob], file.name.replace('.svg', '.png'), {
                type: 'image/png'
              });
              resolve(rasterFile);
            } else {
              reject(new Error('Failed to convert SVG'));
            }
          }, 'image/png');
        };
        img.onerror = () => reject(new Error('Failed to load SVG'));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  const handleFile = async (file) => {
    if (!file) {
      setProfileImageURL(null);
      return;
    }

    if (file.size > maxSize) {
      setError("File size is over the 10MB limit. Please compress the file or upload a smaller file.");
      setProfileImageURL(null);
      return;
    }

    if (!imageTypes.includes(file.type)) {
      setError("Please upload a valid image file.\n(jpeg, jpg, png, svg+xml)");
      setProfileImageURL(null);
      return;
    }

    if (!file.type.startsWith("image/")) {
      setProfileImageURL(null);
      setError("Please select a valid image file.");
      return;
    }

    setError("");
    
    // Convert SVG to raster format for better cropping support
    if (file.type === 'image/svg+xml') {
      try {
        const rasterFile = await convertSvgToRaster(file);
        setTempFile(rasterFile);
        setTempImageSrc(URL.createObjectURL(rasterFile));
      } catch (error) {
        console.error('SVG conversion failed:', error);
        setError('Failed to process SVG file. Please try a different format.');
        setProfileImageURL(null);
        return;
      }
    } else {
      setTempFile(file);
      setTempImageSrc(URL.createObjectURL(file));
    }
    
    setCropperOpen(true);
  };

  const handleFileInput = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFile(file);
      event.target.value = '';
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleCroppedImage = (croppedFile) => {
    handleFileRead(croppedFile);
    onUpload?.(croppedFile);
  };

  const handleSkipCrop = async () => {
    if (tempFile) {
      const processedFile = await processImageForAvatar(tempFile);
      handleFileRead(processedFile);
      onUpload?.(processedFile);
    }
  };

  const resetUpload = () => {
    setProfileImageURL(null);
    setError("");
    setCropperOpen(false);
    setTempImageSrc(null);
    setTempFile(null);
  };

  return {
    profileImageURL,
    setProfileImageURL,
    cropperOpen,
    setCropperOpen,
    tempImageSrc,
    error,
    setError,
    handleFileInput,
    handleDrop,
    handleDragOver,
    handleCroppedImage,
    handleSkipCrop,
    resetUpload,
  };
};